import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import type { RecordModel } from 'pocketbase'
import { ClientResponseError } from 'pocketbase'
import { pb } from '../pocketbase'
import { COL_EMPLOYEES, COL_ROLES } from '../config/collections'
import { normalizePermissions, type PermissionKey } from '../config/permissions'
import { syncLocalEmployeeFromCrm } from '../lib/crmSync'
import { translateErrorMessage } from '../lib/pocketbaseErrorMessage'
import { supabase } from '../lib/supabase'
import type { EmployeeRecord, RoleRecord } from '../types/records'

type AuthContextValue = {
  user: EmployeeRecord | null
  token: string
  loading: boolean
  refresh: () => Promise<void>
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  can: (permission: PermissionKey | string) => boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)

function hydrateUser(record: RecordModel | null): EmployeeRecord | null {
  if (!record) return null
  return record as unknown as EmployeeRecord
}

async function loadEmployeeWithRole(id: string): Promise<EmployeeRecord> {
  try {
    const full = await pb.collection(COL_EMPLOYEES).getOne<EmployeeRecord>(id, {
      expand: 'role',
    })
    return hydrateUser(full)!
  } catch (e) {
    if (!(e instanceof ClientResponseError) || e.status !== 400) {
      throw e
    }
    const row = await pb.collection(COL_EMPLOYEES).getOne<EmployeeRecord>(id)
    const rid = typeof row.role === 'string' ? row.role : ''
    if (rid) {
      try {
        const role = await pb.collection(COL_ROLES).getOne<RoleRecord>(rid)
        row.expand = { ...(row.expand || {}), role }
      } catch {
        /* роль недоступна по правилам — оставляем без expand */
      }
    }
    return hydrateUser(row)!
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<EmployeeRecord | null>(() =>
    hydrateUser(pb.authStore.record),
  )
  const [token, setToken] = useState(() => pb.authStore.token)
  const [loading, setLoading] = useState(!!pb.authStore.token)

  const refresh = useCallback(async () => {
    if (!pb.authStore.isValid || !pb.authStore.record?.id) {
      setUser(null)
      setToken('')
      setLoading(false)
      return
    }
    try {
      const full = await loadEmployeeWithRole(pb.authStore.record.id)
      setUser(full)
      setToken(pb.authStore.token)
      pb.authStore.save(pb.authStore.token, full as unknown as RecordModel)
    } catch {
      pb.authStore.clear()
      setUser(null)
      setToken('')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  useEffect(() => {
    return pb.authStore.onChange(() => {
      setToken(pb.authStore.token)
      setUser(hydrateUser(pb.authStore.record))
    })
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    const trimmed = email.trim()
    if (supabase) {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: trimmed,
        password,
      })
      if (error) {
        throw new Error(translateErrorMessage(error.message || '') || 'Ошибка входа в CRM')
      }
      const accessToken = data.session?.access_token
      if (!accessToken) {
        await supabase.auth.signOut()
        throw new Error('CRM не вернул токен.')
      }
      try {
        await syncLocalEmployeeFromCrm({
          email: trimmed,
          password,
          crmToken: accessToken,
        })
      } catch (syncErr) {
        await supabase.auth.signOut()
        throw syncErr instanceof Error ? syncErr : new Error('Ошибка синхронизации с базой СУП')
      }
      try {
        await pb.collection(COL_EMPLOYEES).authWithPassword(trimmed, password)
      } catch (inner) {
        await supabase.auth.signOut()
        const hint =
          'Вход в CRM выполнен, но локальная учётная запись СУП не принята. Проверьте сервис синхронизации (роль в CRM должна совпадать с name в коллекции roles СУП) или настройте VITE_CRM_SYNC_URL.'
        if (inner instanceof ClientResponseError) {
          const msg = translateErrorMessage(inner.response?.message || inner.message || '')
          throw new Error(`${hint} (${msg})`)
        }
        throw inner instanceof Error ? inner : new Error(hint)
      }
    } else {
      await pb.collection(COL_EMPLOYEES).authWithPassword(trimmed, password)
    }
    await refresh()
  }, [refresh])

  const logout = useCallback(() => {
    void supabase?.auth.signOut()
    pb.authStore.clear()
    setUser(null)
    setToken('')
  }, [])

  const can = useCallback(
    (permission: PermissionKey | string) => {
      if (!user) return false
      if (user.roleName === 'admin') return true
      const role = user.expand?.role as RoleRecord | undefined
      const list = normalizePermissions(role?.permissions)
      if (list.includes('*')) return true
      return list.includes(permission)
    },
    [user],
  )

  const value = useMemo(
    () => ({
      user,
      token,
      loading,
      refresh,
      login,
      logout,
      can,
    }),
    [user, token, loading, refresh, login, logout, can],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth вне AuthProvider')
  return ctx
}
